import { Currency } from '../bank-account/bank-account';
import { EntityKey, ExpenseReceipt, OwnerReference } from '../expense-receipt/expense-receipt';
import { CodedDocumentKey, FrozenApproval, NamedTranslationKey } from '../trip/trip';

/** Mirrors Finance.Models ExpenseClaimStatus. */
export enum ExpenseClaimStatus {
  Draft = 1,
  InApproval = 2,
  AccountingReview = 3,
  Posted = 4,
  /** Expense settlements paid all of it (#5799). */
  Settled = 5,
  /** Expense settlements paid part of it. */
  PartiallySettled = 6,
}

/** The expense claims list tabs — mirrors Finance.Models ExpenseClaimListScope. */
export enum ExpenseClaimListScope {
  All = 0,
  Mine = 1,
  ToApprove = 2,
}

/** A receipt in a claim, with its total in the receipt's and the claim's currency. */
export interface ExpenseClaimReceipt {
  reference: string;
  number: string;
  vendor: CodedDocumentKey | null;
  date: string | null;
  currency: Currency;
  total: number;
  totalUsd: number;
  /** The receipt's total in the claim's currency. */
  claimTotal: number;
  /** The receipt's total in the functional currency, at its own rate. */
  functionalTotal: number;
}

export interface ExpenseClaimStatusHistoryEntry {
  date: string;
  status: ExpenseClaimStatus;
  user: { username: string | null; firstName: string | null; lastName: string | null } | null;
  comment: string | null;
}

/** An expense claim (Rendición de gastos, #5795) — mirrors Finance.Models ExpenseClaimDto. */
export interface ExpenseClaim {
  id: string;
  /** e.g. NT-CLAIM-2026-000001 */
  number: string;
  financialCompanyReference: string;
  /** The fund (70) or trip (66). */
  owner: EntityKey;
  ownerName: string;
  /** The fund's custodian or the trip's employee: never approves the claim. */
  holderUsername: string | null;
  description: string | null;
  /** The fund's currency, or the company's functional currency for a trip. */
  currency: Currency;
  receipts: ExpenseClaimReceipt[];
  total: number;
  totalUsd: number;
  /** The company's books: the total in the functional currency and the rate it is booked at. */
  functionalCurrency: Currency;
  functionalTotal: number;
  exchangeRate: number;
  /** What expense settlements have paid (#5799), in the claim's currency. */
  settledAmount: number;
  outstanding: number;
  claimPolicy: NamedTranslationKey | null;
  approval: FrozenApproval | null;
  submittedBy: string | null;
  status: ExpenseClaimStatus;
  statusHistory: ExpenseClaimStatusHistoryEntry[] | null;
  /** The full receipts (single reads only). */
  receiptDetails: ExpenseReceipt[];
  /** Whether the caller can edit the draft (single reads only). */
  canEdit: boolean;
  /** Whether the caller can approve, reject or take receipts out now (single reads only). */
  canDecide: boolean;
  /** Whether the caller can review and post it now — ClaimAccountingReviewer (single reads only). */
  canReview: boolean;
  /** The single journal entry Accounting posted (#5798). */
  journalEntryReference: string | null;
  journalEntryNumber: string | null;
  postingDate: string | null;
  createdBy: string | null;
  createdDate: string | null;
}

export interface ExpenseClaimSearchCommand {
  search?: string | null;
  statuses?: ExpenseClaimStatus[] | null;
  /** PettyCashFund (70) or Trip (66); null = both. */
  ownerCode?: number | null;
  ownerReference?: string | null;
  scope?: ExpenseClaimListScope;
}

export interface ExpenseClaimCreateCommand {
  owner: OwnerReference;
  description: string | null;
  receiptIds: string[];
}

export interface ExpenseClaimUpdate {
  description: string | null;
}

export interface ExpenseClaimSummary {
  draft: number;
  inApproval: number;
  accountingReview: number;
  toApprove: number;
}

/** One line of the journal entry posting would make (#5798). */
export interface ExpenseClaimJournalLine {
  accountReference: string | null;
  accountNumber: string | null;
  accountName: string | null;
  costCenter: string | null;
  description: string | null;
  debit: number;
  credit: number;
}

/** The journal entry posting would make, and what still blocks it (#5798). */
export interface ExpenseClaimJournalPreview {
  currency: Currency;
  lines: ExpenseClaimJournalLine[];
  totalDebit: number;
  totalCredit: number;
  /** Receipt lines without an expense account, e.g. "NT-RCPT-2026-000004 line 2". */
  missing: string[];
  isReady: boolean;
}
